/**
 * @file       Auth vuex store, used by the whole auth module.
 * @author     Appwapp <info@appwapp.com>
 * @copyright  © Centre collégial de développement de matériel didactique, Collège de Maisonneuve, 2021
 * @license    https://www.ccdmd.qc.ca
 * @version    1.0
 */

import axios from "@/plugins/axios.js";

/**
 * Gets the default state.
 *
 * @return  {Object}  The default state.
 */
function getDefaultState() {
  return {
    profile: {
      status: null,
      data: null,
      error: null
    },
    password: {
      status: null,
      data: null,
      error: null
    },
    forgotPassword: {
      status: null,
      data: null,
      error: null
    },
    register: {
      status: null,
      data: null,
      error: null
    }
  };
}

/**
 * Auth vuex actions.
 *
 * @type  {Object}
 */
const actions = {
  /**
   * Login.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  login: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onLogin");

      axios
        .post(`login`, params)
        .then(response => {
          commit("onLoginSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onLoginError", error);
          reject(error);
        });
    });
  },

  /**
   * Set the password.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  setPassword: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onSetPassword");

      const hash = params.hash;
      delete params.hash;

      axios
        .post(`setPassword/${hash}`, params)
        .then(response => {
          commit("onSetPasswordSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onSetPasswordError", error);
          reject(error);
        });
    });
  },

  /**
   * Logout.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  logout: ({ commit }) => {
    return new Promise(resolve => {
      commit("onLogout");
      resolve();
    });
  },

  /**
   * Set new password.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  newPassword: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onNewPassword");
      axios
        .post(`forgotPassword`, params)
        .then(response => {
          commit("onNewPasswordSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onNewPasswordError", error);
          reject(error);
        });
    });
  },

  /**
   * Check the auth token.
   *
   * @param   {Function}  arg1.commit  Commit the action
   *
   * @return  {Promise}
   */
  checkToken: ({ commit }) => {
    return new Promise((resolve, reject) => {
      commit("onCheckToken");

      const refresh_token = localStorage.getItem("refresh-token");
      axios
        .post(`auth/checkToken`, { refresh_token })
        .then(response => {
          commit("onCheckTokenSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onCheckTokenError", error);
          reject(error);
        });
    });
  },

  /**
   * Register.
   *
   * @param   {Function}  arg1.commit  Commit the action
   * @param   {Object}    params       The parameters
   *
   * @return  {Promise}
   */
  register: ({ commit }, params) => {
    return new Promise((resolve, reject) => {
      commit("onRegister");

      axios
        .post(`register`, params)
        .then(response => {
          commit("onRegisterSuccess", response);
          resolve(response);
        })
        .catch(error => {
          commit("onRegisterError", error);
          reject(error);
        });
    });
  }
};

/**
 * Auth vuex mutations.
 *
 * @type  {Object}
 */
const mutations = {
  /**
   * Commit login.
   *
   * @param  {Object}  state   The vuex state
   */
  onLogin: state => {
    state.profile.status = "fetching";
    state.profile.error = {};
  },

  /**
   * Commit login success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onLoginSuccess: (state, { data }) => {
    // Set the auth token in localStorage
    localStorage.setItem("access-token", data.auth.access_token);
    localStorage.setItem("refresh-token", data.auth.refresh_token);
    state.profile.status = "success";
    state.profile.error = null;
    state.profile.data = data;
  },

  /**
   * Commit login error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onLoginError: (state, err) => {
    state.profile.status = "error";
    state.profile.error = err;
  },

  /**
   * Commit set password.
   *
   * @param  {Object}  state   The vuex state
   */
  onSetPassword: state => {
    state.password.status = "fetching";
    state.password.errors = {};
  },

  /**
   * Commit set password success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onSetPasswordSuccess: (state, { data }) => {
    state.password.status = "success";
    state.password.errors = {};
    state.password.data = data;
  },

  /**
   * Commit set password error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onSetPasswordError: (state, err) => {
    state.password.status = "error";
    state.password.errors = err;
  },

  /**
   * Commit logout.
   *
   * @param  {Object}  state   The vuex state
   */
  onLogout: state => {
    // Reset profile and password data
    state.profile.status = null;
    state.profile.data = null;
    state.profile.error = null;
    state.password.status = null;
    state.password.data = null;
    state.password.error = null;

    // Remove access token from local storage
    localStorage.removeItem("access-token");
  },

  /**
   * Commit new password.
   *
   * @param  {Object}  state   The vuex state
   */
  onNewPassword: state => {
    state.forgotPassword.status = "fetching";
    state.forgotPassword.errors = {};
  },

  /**
   * Commit new password success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onNewPasswordSuccess: (state, { data }) => {
    state.forgotPassword.status = "success";
    state.forgotPassword.errors = {};
    state.forgotPassword.data = data;
  },

  /**
   * Commit new password error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onNewPasswordError: (state, err) => {
    state.forgotPassword.status = "error";
    state.forgotPassword.errors = err;
  },

  /**
   * Commit check token.
   *
   * @param  {Object}  state   The vuex state
   */
  onCheckToken: state => {
    state.profile.status = "fetching";
    state.profile.errors = {};
  },

  /**
   * Commit check token success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onCheckTokenSuccess: (state, { data }) => {
    // Set the auth token in localStorage
    localStorage.setItem("access-token", data.token.access_token);
    localStorage.setItem("refresh-token", data.token.refresh_token);
    state.profile.status = "success";
    state.profile.error = null;
    state.profile.data = data;
  },

  /**
   * Commit check token error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onCheckTokenError: (state, err) => {
    state.profile.status = "error";
    state.profile.errors = err;
  },

  /**
   * Commit register.
   *
   * @param  {Object}  state   The vuex state
   */
  onRegister: state => {
    state.register.status = "fetching";
    state.register.errors = {};
  },

  /**
   * Commit register success.
   *
   * @param  {Object}  state     The vuex state
   * @param  {Object}  arg2.data The data returned
   */
  onRegisterSuccess: (state, { data }) => {
    state.register.status = "success";
    state.register.error = null;
    state.register.data = data;
  },

  /**
   * Commit register error.
   *
   * @param  {Object}  state   The vuex state
   * @param  {Object}  err     The error
   */
  onRegisterError: (state, err) => {
    state.register.status = "error";
    state.register.errors = err;
  }
};

/**
 * Export the store.
 *
 * @type  {Object}
 */
export default {
  namespaced: true,
  state: getDefaultState(),
  actions,
  mutations
};
